import { ComponentProps, ReactNode, useMemo } from 'react';
import { css } from '@emotion/react';
import { Weekday } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { DatePickerField, DaysOfWeek, DropdownField, Text, TimeField } from '@frontend/design-system';
import { DEFAULT_TIME_FIELD_SCROLL_TO_TIME } from '../../../../constants';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import { getRRuleDayFromDaysOfWeek } from '../../../../rrule-helper';
import { AvailabilityFormRow } from '../atoms/availability-form-row';
import { DayPickerWithSelectedDays } from '../atoms/day-picker-with-selected-days';
import { MonthlyRecurrenceRow } from '../atoms/monthly-recurrence-row';
import { PreviewNotification } from '../atoms/preview-notification';
import { RepeatFrequencyDropdown } from '../atoms/repeat-frequency-dropdown';
import { getPreviewPatternDescription, PreviewPatternFormValues } from '../helpers/get-preview-pattern-description';
import { availabilityRecurrenceOptions, repeatFrequencyOptions } from '../types';

type RecurrenceFieldsFormValues = PreviewPatternFormValues & {
  recurrenceType?: string;
};

type FieldPropsGetter = (...args: any[]) => Record<string, unknown>;
type DropdownFieldProps = Omit<ComponentProps<typeof DropdownField>, 'label' | 'children'>;
type DatePickerFieldProps = Omit<ComponentProps<typeof DatePickerField>, 'label'>;
type TimeFieldProps = Omit<ComponentProps<typeof TimeField>, 'label'>;

type Props<TFieldProps extends FieldPropsGetter, TValues extends RecurrenceFieldsFormValues> = {
  locationId: string | undefined;
  formProps: ReturnType<typeof useRuleRecurrenceForm<TFieldProps, TValues>>;
  previewPatternFormValues: PreviewPatternFormValues;
  endDateLabel?: ReactNode;
  timeFieldsDisabled?: boolean;
  additionalTimeRowContent?: ReactNode;
};

export const RecurrenceFields = <TFieldProps extends FieldPropsGetter, TValues extends RecurrenceFieldsFormValues>({
  locationId,
  formProps,
  previewPatternFormValues,
  endDateLabel,
  timeFieldsDisabled = false,
  additionalTimeRowContent,
}: Props<TFieldProps, TValues>) => {
  const { t } = useTranslation('schedule-settings');
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezone = locationTimezoneMap[locationId ?? '']?.timezone;

  const {
    recurrenceFormValues,
    recurrenceType,
    daysOfWeek,
    repeatOn,
    rRule,
    setDaysOfWeek,
    setRepeatOn,
    getRecurrenceFormFieldProps,
    getMonthlyRecurrenceFormFieldProps,
    getAvailabilityRuleFieldProps,
  } = formProps;

  const recurrenceTypeFieldProps = getAvailabilityRuleFieldProps('recurrenceType') as DropdownFieldProps;
  const startDateFieldProps = getAvailabilityRuleFieldProps('startDate') as DatePickerFieldProps;
  const endDateFieldProps = getAvailabilityRuleFieldProps('endDate') as DatePickerFieldProps;
  const startTimeFieldProps = getAvailabilityRuleFieldProps('startTime') as TimeFieldProps;
  const endTimeFieldProps = getAvailabilityRuleFieldProps('endTime') as TimeFieldProps;

  const { repeatFrequencyType } = recurrenceFormValues;
  const previewPatternDescription = useMemo(
    () =>
      getPreviewPatternDescription({
        recurrenceType,
        repeatFrequencyType,
        rRule,
        formValues: previewPatternFormValues,
        timezone,
      }),
    [recurrenceType, repeatFrequencyType, rRule, previewPatternFormValues, timezone]
  );

  const handleDayToggle =
    (setState: React.Dispatch<React.SetStateAction<Weekday[]>>) =>
    ({ selectedDays }: { selectedDays: DaysOfWeek[] }) => {
      setState(selectedDays.map(getRRuleDayFromDaysOfWeek));
    };

  return (
    <>
      <AvailabilityFormRow>
        <DropdownField label={t('Recurrence')} {...recurrenceTypeFieldProps} hasNonFloatingLabel>
          {availabilityRecurrenceOptions?.map((item) => (
            <DropdownField.Option key={item.id} value={item.id}>
              {item.displayName}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </AvailabilityFormRow>
      {recurrenceType === 'SPECIFIC_DATE' && (
        <AvailabilityFormRow>
          <DatePickerField label={t('Date')} {...startDateFieldProps} hasNonFloatingLabel css={{ maxWidth: 'none' }} />
        </AvailabilityFormRow>
      )}
      {recurrenceType === 'WEEKLY' && (
        <AvailabilityFormRow title={t('Day(s) of the week')}>
          <DayPickerWithSelectedDays daysPicked={daysOfWeek} onDayToggle={handleDayToggle(setDaysOfWeek)} />
        </AvailabilityFormRow>
      )}
      {recurrenceType === 'CUSTOM' && (
        <>
          <AvailabilityFormRow title={t('Repeat Pattern')} css={repeatPatternRowStyles}>
            <Text>{t('Every')}</Text>
            <RepeatFrequencyDropdown getFieldProps={getRecurrenceFormFieldProps} />
            <DropdownField label={''} {...getRecurrenceFormFieldProps('repeatFrequencyType')}>
              {repeatFrequencyOptions?.map((item) => (
                <DropdownField.Option key={item.id} value={item.id}>
                  {item.displayName}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </AvailabilityFormRow>
          {recurrenceFormValues.repeatFrequencyType === 'weeks' && (
            <AvailabilityFormRow title={t('Repeat On')}>
              <DayPickerWithSelectedDays daysPicked={repeatOn} onDayToggle={handleDayToggle(setRepeatOn)} />
            </AvailabilityFormRow>
          )}
          {recurrenceFormValues.repeatFrequencyType === 'months' && (
            <MonthlyRecurrenceRow getFieldProps={getMonthlyRecurrenceFormFieldProps} />
          )}
        </>
      )}
      {recurrenceType !== 'SPECIFIC_DATE' && (
        <AvailabilityFormRow>
          <DatePickerField label={t('Start Date')} {...startDateFieldProps} hasNonFloatingLabel />
          <DatePickerField label={endDateLabel ?? t('End Date')} {...endDateFieldProps} hasNonFloatingLabel />
        </AvailabilityFormRow>
      )}
      {additionalTimeRowContent}
      <AvailabilityFormRow>
        <TimeField
          label={t('Start Time')}
          {...startTimeFieldProps}
          disabled={timeFieldsDisabled}
          initialScrollTime={DEFAULT_TIME_FIELD_SCROLL_TO_TIME}
          hasNonFloatingLabel
        />
        <TimeField
          label={t('End Time')}
          {...endTimeFieldProps}
          disabled={timeFieldsDisabled}
          initialScrollTime={DEFAULT_TIME_FIELD_SCROLL_TO_TIME}
          hasNonFloatingLabel
        />
      </AvailabilityFormRow>
      {recurrenceType === 'CUSTOM' && (
        <AvailabilityFormRow>
          <PreviewNotification title={t('Pattern preview:')} description={previewPatternDescription} />
        </AvailabilityFormRow>
      )}
    </>
  );
};

const repeatPatternRowStyles = css({
  display: 'flex',
  alignItems: 'center',

  '> *': {
    flexBasis: 0,
    flexShrink: 1,
  },
  '> *:last-child': {
    flex: 1,
  },
});
